import { ArrowLeft } from "lucide-react";
import { Button } from '@/components/ui/button';
import therappiLogo from '@/assets/therappi-text-logo.svg';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';
import { LogoEditor } from '@/components/LogoEditor';
import { useLogoEditor } from '@/contexts/LogoEditorContext';
import { logoConfig } from '@/config/logoSizes';

interface PageHeaderProps {
  title?: string;
  showBackButton?: boolean;
  backTo?: string; // Optional: force a specific back destination
  children?: React.ReactNode;
}

export const PageHeader = ({ title, showBackButton = true, backTo, children }: PageHeaderProps) => {
  const { goBack, getBackButtonLabel } = useNavigationHistory();
  const { isEditing } = useLogoEditor();

  const handleBack = () => {
    if (backTo) {
      window.location.href = backTo;
    } else {
      goBack();
    }
  };

  return (
    <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          {/* Left side: Logo */}
          <div className="flex items-center gap-4">
            {isEditing ? (
              <LogoEditor logoId="page-header-therappi" className={logoConfig.header.mobile}>
                <img src={therappiLogo} alt="Therappi®" className="w-full h-full object-contain" />
              </LogoEditor>
            ) : (
              <img 
                src={therappiLogo} 
                alt="Therappi®"
                className={`${logoConfig.header.mobile} object-contain`}
              />
            )}
            
            {title && (
              <h1 className="text-lg sm:text-xl font-semibold text-foreground hidden sm:block">
                {title}
              </h1>
            )}
          </div>

          {/* Right side: Back button and optional children */}
          <div className="flex items-center gap-2">
            {showBackButton && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">{backTo ? 'Dashboard' : getBackButtonLabel()}</span>
              </Button>
            )}
            {children}
          </div>
        </div>
        
        {/* Mobile title - show below on small screens */}
        {title && (
          <h1 className="text-lg font-semibold text-foreground mt-2 sm:hidden">
            {title}
          </h1>
        )}
      </div>
    </header>
  );
};
